/** Состояние партии на клиенте.
 *
 * Ход применяется локально сразу, а на сервер уходит очередью — иначе на
 * мобильной сети каждая цифра появлялась бы с задержкой. Сервер остаётся
 * источником истины: его ответ заменяет состояние целиком.
 *
 * Очередь строго последовательная. Это не оптимизация, а требование
 * протокола: ходы нумеруются подряд, и отправить пятый раньше четвёртого
 * значит получить конфликт. Пока ход не подтверждён, он остаётся в очереди,
 * поэтому обрыв связи не теряет прогресс — запрос повторится.
 */

import { useCallback, useEffect, useRef, useState } from 'react'

import { ApiError, NetworkError } from '../api/client'
import { redoMove, requestHint, sendMove, undoMove } from '../api/endpoints'
import type {
  GameState,
  Hint,
  HintKind,
  MoveKind,
  MoveResult,
  Progress,
  ScoreBreakdown,
} from '../api/types'
import { applyMove, type BoardSnapshot } from './board'

/** Пауза перед повтором запроса, если пропала связь. */
const RETRY_DELAY_MS = 1500

type Operation =
  | { type: 'move'; seq: number; cell: number; kind: MoveKind; value: number }
  | { type: 'undo' }
  | { type: 'redo' }

export interface GameController {
  game: GameState
  /** Доска с учётом ещё не подтверждённых ходов. */
  snapshot: BoardSnapshot
  pendingCount: number
  offline: boolean
  error: string | null
  /** Клетка, где сервер только что нашёл ошибку, — для подсветки. */
  mistakeCell: number | null
  finalScore: ScoreBreakdown | null
  /** Серия, рекорд и достижения — приходят вместе с победой. */
  finalProgress: Progress | null
  /** Кристаллы за прогресс — приходят вместе с поражением. */
  progressCoins: number
  /** Последняя показанная подсказка. */
  hint: Hint | null
  play: (cell: number, kind: MoveKind, value: number) => void
  undo: () => void
  redo: () => void
  askHint: (kind: HintKind, cell?: number) => Promise<number>
  /** Принять состояние партии, изменённое в обход очереди ходов. */
  adoptGame: (game: GameState) => void
  dismissHint: () => void
  dismissError: () => void
}

export interface GameOptions {
  /**
   * Повторять ли на клиенте автоочистку заметок.
   *
   * Настройка приходит из профиля и обязана совпадать с серверной: если
   * сервер чистит заметки соседей, а клиент нет, картинка будет дёргаться
   * на каждом ходу.
   */
  autoClearNotes?: boolean
}

export function useGame(initial: GameState, options: GameOptions = {}): GameController {
  const [game, setGame] = useState<GameState>(initial)
  const [queue, setQueue] = useState<Operation[]>([])
  const [offline, setOffline] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [mistakeCell, setMistakeCell] = useState<number | null>(null)
  const [finalScore, setFinalScore] = useState<ScoreBreakdown | null>(null)
  const [finalProgress, setFinalProgress] = useState<Progress | null>(null)
  const [progressCoins, setProgressCoins] = useState(0)
  const [hint, setHint] = useState<Hint | null>(null)

  const gameRef = useRef(game)
  const queueRef = useRef<Operation[]>([])
  const runningRef = useRef(false)
  const retryRef = useRef<number | null>(null)

  gameRef.current = game

  const syncQueue = () => setQueue([...queueRef.current])

  const handleResult = useCallback((result: MoveResult) => {
    setGame(result.game)
    if (result.finished && result.score) {
      setFinalScore(result.score)
      setFinalProgress(result.progress)
    }
    if (result.finished && result.progress_coins) {
      setProgressCoins(result.progress_coins)
    }
  }, [])

  const execute = useCallback(async (operation: Operation): Promise<MoveResult> => {
    const gameId = gameRef.current.id
    if (operation.type === 'undo') return undoMove(gameId)
    if (operation.type === 'redo') return redoMove(gameId)
    return sendMove(gameId, {
      seq: operation.seq,
      cell: operation.cell,
      kind: operation.kind,
      value: operation.value,
    })
  }, [])

  const drain = useCallback(async () => {
    if (runningRef.current) return
    runningRef.current = true

    try {
      while (queueRef.current.length > 0) {
        const operation = queueRef.current[0]
        try {
          const result = await execute(operation)
          setOffline(false)
          if (operation.type === 'move' && result.is_mistake) {
            setMistakeCell(operation.cell)
          }
          handleResult(result)
          queueRef.current.shift()
          syncQueue()
        } catch (failure) {
          if (failure instanceof NetworkError) {
            // Ход остаётся в очереди: связь вернётся — отправим снова.
            setOffline(true)
            retryRef.current = window.setTimeout(() => void drain(), RETRY_DELAY_MS)
            return
          }

          if (failure instanceof ApiError) {
            // Сервер прислал своё состояние — принимаем его и начинаем заново.
            if (failure.body.game) {
              setGame(failure.body.game)
              queueRef.current = []
              syncQueue()
            } else {
              queueRef.current.shift()
              syncQueue()
            }
            if (failure.code !== 'move_out_of_sync') {
              setError(failure.message)
            }
            continue
          }

          queueRef.current.shift()
          syncQueue()
          setError('Не удалось выполнить ход')
        }
      }
    } finally {
      runningRef.current = false
    }
  }, [execute, handleResult])

  const enqueue = useCallback(
    (operation: Operation) => {
      queueRef.current.push(operation)
      syncQueue()
      void drain()
    },
    [drain],
  )

  const play = useCallback(
    (cell: number, kind: MoveKind, value: number) => {
      if (gameRef.current.status !== 'active') return
      setMistakeCell(null)
      const queuedMoves = queueRef.current.filter((item) => item.type === 'move').length
      enqueue({
        type: 'move',
        // Номер хода складывается из подтверждённых и уже стоящих в очереди.
        seq: gameRef.current.move_count + queuedMoves + 1,
        cell,
        kind,
        value,
      })
    },
    [enqueue],
  )

  // Отмену и повтор не применяем локально: чтобы откатить ход, нужно знать
  // его прежнее значение, а оно есть только на сервере. Кнопки на время
  // синхронизации блокируются.
  const undo = useCallback(() => enqueue({ type: 'undo' }), [enqueue])
  const redo = useCallback(() => enqueue({ type: 'redo' }), [enqueue])

  /**
   * Запросить подсказку. Возвращает новый баланс.
   *
   * Подсказка идёт мимо очереди ходов и требует, чтобы очередь была пуста:
   * на сервере она сама становится ходом со следующим номером, и с
   * неотправленными ходами номера бы столкнулись. Кнопка на это время
   * блокируется — при обычной задержке сети это незаметно.
   */
  const askHint = useCallback(async (kind: HintKind, cell?: number) => {
    const current = gameRef.current
    // Ключ зависит от числа уже полученных подсказок, поэтому два быстрых
    // нажатия подряд дадут один и тот же ключ — и одно списание.
    const requestKey = `${current.id}:${current.hints_used}:${kind}:${cell ?? ''}`

    const result = await requestHint(current.id, kind, { cell, requestKey })
    setGame(result.game)
    setHint(result.hint)
    if (result.score) {
      setFinalScore(result.score)
      setFinalProgress(result.progress)
    }
    return result.balance
  }, [])

  useEffect(
    () => () => {
      if (retryRef.current) window.clearTimeout(retryRef.current)
    },
    [],
  )

  const snapshot = queue.reduce<BoardSnapshot>(
    (state, operation) =>
      operation.type === 'move'
        ? applyMove(state, operation, { autoClearNotes: options.autoClearNotes })
        : state,
    { board: game.board, notes: game.notes },
  )

  return {
    game,
    snapshot,
    pendingCount: queue.length,
    offline,
    error,
    mistakeCell,
    finalScore,
    finalProgress,
    progressCoins,
    hint,
    play,
    undo,
    redo,
    askHint,
    adoptGame: setGame,
    dismissHint: () => setHint(null),
    dismissError: () => setError(null),
  }
}

/** Секундомер партии: серверное время плюс то, что натикало с ответа. */
export function useElapsed(game: GameState): number {
  const [now, setNow] = useState(() => Date.now())
  const baseRef = useRef({ elapsed: game.elapsed_ms, at: Date.now() })

  useEffect(() => {
    baseRef.current = { elapsed: game.elapsed_ms, at: Date.now() }
    setNow(Date.now())
  }, [game.elapsed_ms])

  useEffect(() => {
    if (game.status !== 'active' || game.is_paused) return
    const timer = window.setInterval(() => setNow(Date.now()), 1000)
    return () => window.clearInterval(timer)
  }, [game.status, game.is_paused])

  // На паузе показываем ровно то, что насчитал сервер: локальный отсчёт
  // тут же разошёлся бы с ним, и после возврата время бы прыгнуло.
  if (game.status !== 'active' || game.is_paused) return game.elapsed_ms
  return baseRef.current.elapsed + (now - baseRef.current.at)
}
